import { Col } from "@amzn/stencil-react-components/layout";
import { Select } from "@amzn/stencil-react-components/select";
import { Label } from "@amzn/stencil-react-components/text";
import { getString } from "common/uistringlabels/uiStringUtils";
import React, { FC } from "react";

/**
 * Props interface for the LicenseSelect component.
 * @interface
 * @property {(newPrice?: License) => void} updatePrice - Function to update the price based on the selected license.
 * @property {License | null} selectedLicense - The currently selected license or null if none is selected.
 * @property {License[]} licenses - Array of available licenses to choose from.
 */
interface LicenseSelectProps {
  updateSelectedLicense: (newPrice?: License) => void;
  selectedLicense: License | null;
  licenses: License[];
}

/**
 * Interface representing a software license option.
 * @interface
 * @property {string} licenseType - The type of license.
 * @property {string} licenseName - The display name of the license.
 * @property {number} price - The price of the license.
 */
export interface License {
  licenseType: string;
  licenseName: string;
  price: number;
}

const DEFAULT_LICENSE_INDEX = 0;

export const LicenseSelect: FC<LicenseSelectProps> = ({
  updateSelectedLicense,
  selectedLicense,
  licenses,
}: LicenseSelectProps) => {
  const handleSelectChange = (selectedValue: string) => {
    const newSelectedLicense: License | undefined = licenses?.find((license) => license.licenseName === selectedValue);
    updateSelectedLicense(newSelectedLicense);
  };

  const options = licenses.map((license) => license.licenseName);

  return (
    <Col gridGap="S200">
      <Label>{getString("details.selectLicense")}</Label>
      <Select
        id="select-license-type"
        dataTestId="select-license-type"
        onChange={handleSelectChange}
        options={options}
        value={selectedLicense ? selectedLicense.licenseName : licenses[DEFAULT_LICENSE_INDEX].licenseName}
        width="65%"
      />
    </Col>
  );
};
